import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Header from "../../components/Header";
import { SquareLoader } from "react-spinners";
import RoomDropdown from "../../components/RoomDropdown/RoomDropdown";
import { useTranslation } from "react-i18next";
import { getApiEndpoint } from "../../utils/apiConfig";
import axiosWithAuth from "../../utils/axiosWithAuth";
import { getTemporaryCalculation } from "../../services/repairService";
import axios from 'axios';
import './AllMaterials.css';

const AllMaterials = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  let { id } = useParams();
  const location = useLocation();
  
  // Parse URL parameters
  const queryParams = new URLSearchParams(location.search);
  const queryId = queryParams.get('id');
  const isBreakdown = queryParams.get('breakdown') !== 'false';
  
  // Use ID from params or query
  id = id || queryId;
  
  console.log('AllMaterials DEBUG - ID Sources:', {
    paramsId: useParams().id,
    queryId: queryId,
    finalId: id,
    currentURL: window.location.href,
    searchParams: location.search
  });
  
  document.title = t("material.0");
  const headerTitle = t("material.0");

  const [property] = useState(null);
  const [breakdownData, setBreakdownData] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [filteredRooms, setFilteredRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRedirectingToLogin, setIsRedirectingToLogin] = useState(false);

  const [costs, setCosts] = useState(null);
  const [materialDetails, setMaterialDetails] = useState({});
  const [doorWindowPrices, setDoorWindowPrices] = useState({
    doorPrice: 200,
    doorServicePrice: 50,
    windowPrice: 300,
    windowServicePrice: 50
  });

  const backwardNavigate = `/`;
  
  // Get token from multiple sources for authentication
  const accessToken = localStorage.getItem("accessToken") || 
                      sessionStorage.getItem("accessToken") || 
                      localStorage.getItem("token");

  // Store token globally for debugging
  window.accessToken = accessToken;

  // Check if user is logged in and redirect if not
  useEffect(() => {
    const isLoggedIn = !!accessToken;
    
    if (!isLoggedIn) {
      console.log("User is not logged in, redirecting to login page");
      setIsRedirectingToLogin(true);
      
      // Save current path for redirect after login
      const currentPath = window.location.pathname + window.location.search;
      
      // Check if user is coming from Material page
      const referrer = document.referrer;
      const isFromMaterialPage = referrer && referrer.includes('/material');
      if (isFromMaterialPage) {
        localStorage.setItem('comingFromMaterialPage', 'true');
        console.log("User is coming from Material page:", referrer);
      }
      
      // Store any temporary material data before redirecting
      try {
        if (id) {
          localStorage.setItem('pendingRepairId', id);
          localStorage.setItem('redirectToAllMaterials', 'true');
          console.log("Storing material ID before login redirect:", id);
          
          // If it's a temporary ID, store the data
          if (id.startsWith('temp-')) {
            const tempData = localStorage.getItem(id);
            if (tempData) {
              localStorage.setItem('lastRepairData', tempData);
              
              try {
                const parsedData = JSON.parse(tempData);
                
                if (parsedData.property) {
                  localStorage.setItem('lastRepairProperty', JSON.stringify(parsedData.property));
                }
                
                if (parsedData.materials) {
                  localStorage.setItem('lastRepairMaterials', JSON.stringify(parsedData.materials));
                }
                
                if (parsedData.costs) {
                  localStorage.setItem('lastRepairMaterialCost', parsedData.costs.materialsCost || 0);
                  localStorage.setItem('lastRepairServiceCost', parsedData.costs.serviceCost || 0);
                  localStorage.setItem('lastRepairGeneralCost', parsedData.costs.generalCost || 0);
                }
              } catch (parseError) {
                console.error("Error parsing temp data before login:", parseError);
              }
            }
          }
        }
        
        // Also store any selected material data from session storage
        const selectedMaterialData = sessionStorage.getItem('selectedMaterial');
        if (selectedMaterialData) {
          localStorage.setItem('pendingSelectedMaterial', selectedMaterialData);
        }
      } catch (e) {
        console.error("Error saving material data before login redirect:", e);
      }
      
      // Short delay to show the redirect message
      setTimeout(() => {
        navigate('/login', { state: { returnUrl: currentPath } });
      }, 1000);
      return;
    }
    
    console.log("User is logged in, proceeding to load materials");
  }, [accessToken, navigate, id]);

  // Fetch material details to get store information
  const fetchMaterialDetails = async (breakdownData) => {
    if (!breakdownData || !breakdownData.breakdown || !breakdownData.breakdown.rooms) {
      console.log("No breakdown data to fetch material details for");
      return {};
    }

    const allMaterialIds = [];
    
    // Collect all material IDs that need fetching
    breakdownData.breakdown.rooms.forEach(room => {
      if (room.materials && Array.isArray(room.materials)) {
        room.materials.forEach(material => {
          const materialId = material.materialId || material.id;
          if (materialId && materialId !== 'door-material' && materialId !== 'window-material' && !allMaterialIds.includes(materialId)) {
            allMaterialIds.push(materialId);
          }
        });
      }
    });

    console.log(`AllMaterials: Fetching details for ${allMaterialIds.length} materials:`, allMaterialIds);
    
    const materialDetails = {};
    
    // Fetch details for each material with error handling
    for (const materialId of allMaterialIds) {
      try {
        const response = await axios.get(
          getApiEndpoint(`materials/${materialId}`), 
          { 
            headers: { 
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          }
        );
        materialDetails[materialId] = response.data;
        console.log(`AllMaterials: Successfully fetched details for material ${materialId}:`, {
          stores: response.data.stores,
          storeCount: response.data.stores?.length || 0
        });
      } catch (error) {
        console.error(`AllMaterials: Failed to fetch material details for ${materialId}:`, error);
        
        // Add a placeholder for the missing material
        materialDetails[materialId] = {
          id: materialId,
          name: { text: 'Material not found', az: 'Materyal tapılmadı' },
          price: 0,
          servicePrice: 0,
          currency: 'AZN',
          stores: [],
          error: true
        };
      }
    }
    
    console.log("AllMaterials: Final materialDetails state:", materialDetails);
    setMaterialDetails(materialDetails);
    
    return materialDetails;
  };

  // Fetch door and window prices from settings
  const fetchDoorWindowPrices = async () => {
    try {
      // Get repair style and tariff IDs from breakdown data or localStorage
      let styleId = null;
      let tariffId = null;

      // Try to get from breakdown data first
      if (breakdownData?.breakdown?.repairStyle) {
        styleId = breakdownData.breakdown.repairStyle._id || breakdownData.breakdown.repairStyle.id;
      }
      if (breakdownData?.breakdown?.repairTariff) {
        tariffId = breakdownData.breakdown.repairTariff._id || breakdownData.breakdown.repairTariff.id;
      }

      // If not in breakdown, try localStorage repairData
      if (!styleId || !tariffId) {
        const repairData = JSON.parse(localStorage.getItem('repairData') || '{}');
        if (repairData.repairStyle) {
          styleId = repairData.repairStyle._id || repairData.repairStyle.id || repairData.repairStyle;
        }
        if (repairData.repairTariff) {
          tariffId = repairData.repairTariff._id || repairData.repairTariff.id || repairData.repairTariff;
        }
      }

      // If still no IDs, try homeData in sessionStorage
      if (!styleId || !tariffId) {
        const homeData = JSON.parse(sessionStorage.getItem('homeData') || '{}');
        if (homeData.repairStyle) {
          styleId = homeData.repairStyle._id || homeData.repairStyle.id || homeData.repairStyle;
        }
        if (homeData.repairTariff) {
          tariffId = homeData.repairTariff._id || homeData.repairTariff.id || homeData.repairTariff;
        }
      }

      console.log('AllMaterials: Fetching door/window prices for:', { styleId, tariffId });

      if (!styleId || !tariffId) {
        console.warn('AllMaterials: Missing styleId or tariffId, using fallback values');
        return;
      }

      const response = await axios.get(getApiEndpoint('settings/prices'), {
        params: { styleId, tariffId },
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (response.data && response.data.prices) {
        console.log('AllMaterials: Door/window prices response:', response.data.prices);

        setDoorWindowPrices({
          doorPrice: parseFloat(response.data.prices.door?.price) || 200,
          doorServicePrice: parseFloat(response.data.prices.door?.servicePrice) || 50,
          windowPrice: parseFloat(response.data.prices.window?.price) || 300,
          windowServicePrice: parseFloat(response.data.prices.window?.servicePrice) || 50
        });
      }
    } catch (error) {
      console.error('AllMaterials: Failed to fetch door/window prices:', error);
      // Keep default values when API fails
    }
  };

  // Only navigate to home if no ID
  useEffect(() => {
    if (!id) {
      navigate("/");
      return;
    }
  }, [id, navigate]);

  useEffect(() => {
    // For non-temporary IDs without token, still don't force redirect
    // We'll show the content and handle login logic elsewhere
    // Only navigate away if there's no ID at all
    if (!id) {
      navigate("/");
      return;
    }
    
    // Prevent multiple fetches if we already have data
    if (breakdownData && rooms.length > 0) {
      return;
    }

    const fetchData = async () => {
      try {
      setIsLoading(true);
      setError(null);
      
        // Handle temporary IDs
        if (id && id.startsWith('temp-')) {
          
          console.log('AllMaterials DEBUG - Handling temporary ID:', {
            tempId: id,
            localStorageKeys: Object.keys(localStorage).filter(key => key.startsWith('temp-')),
            sessionStorageSelectedMaterial: !!sessionStorage.getItem('selectedMaterial')
          });
          
          // First try to get data from temporary storage
          const tempCalculation = getTemporaryCalculation(id);
          console.log('AllMaterials DEBUG - getTemporaryCalculation result:', {
            tempId: id,
            foundData: !!tempCalculation,
            hasBreakdown: !!(tempCalculation && tempCalculation.breakdown),
            dataKeys: tempCalculation ? Object.keys(tempCalculation) : null
          });
          
          if (tempCalculation && tempCalculation.breakdown) {
            setBreakdownData(tempCalculation);
            // Add defensive checks to avoid accessing undefined properties
            setRooms(tempCalculation.breakdown?.rooms || []);
            setFilteredRooms(tempCalculation.breakdown?.rooms || []);
            setCosts(tempCalculation.costs || {});
            
            // Fetch material details for store information
            await fetchMaterialDetails(tempCalculation);
            
            // Fetch door and window prices
            await fetchDoorWindowPrices();
            
            setIsLoading(false);
            return;
          }
          
          // If no temp calculation, try selectedMaterial
          const selectedMaterialData = sessionStorage.getItem('selectedMaterial');
          if (selectedMaterialData) {
            try {
              const parsedMaterial = JSON.parse(selectedMaterialData);
              
              // Get fixtures from sessionStorage
              const sessionFixtures = JSON.parse(sessionStorage.getItem("fixtures") || 'null');
              const initialFixtures = sessionFixtures || {
                doors: { count: 1, materialCost: 200, serviceCost: 50, name: "Doors" },
                windows: { count: 1, materialCost: 300, serviceCost: 50, name: "Windows" }
              };

              // Ensure parsedMaterial and roomTypes exist before proceeding
              if (!parsedMaterial || !parsedMaterial.roomTypes || !Array.isArray(parsedMaterial.roomTypes)) {
                setError("Material məlumatları zədələnib");
                setIsLoading(false);
                return;
              }
              
              // Create breakdown structure from material data
              const breakdownData = {
                breakdown: {
                  rooms: parsedMaterial.roomTypes.map(room => {
                    // Calculate room area if not explicitly provided
                    let roomArea = parseFloat(room.area || 0);
                    
                    // If area is invalid or zero, calculate from materials
                    if (isNaN(roomArea) || roomArea <= 0) {
                      // Sum up all material areas
                      roomArea = room.materials.reduce((sum, mat) => {
                        // Use the material's area value or default to 1
                        const materialArea = parseFloat(mat.area || 0);
                        return sum + (isNaN(materialArea) || materialArea <= 0 ? 1 : materialArea);
                      }, 0);
                      
                      // If still zero, default to 1
                      roomArea = roomArea <= 0 ? 1 : roomArea;
                    }
                    
                    // Calculate room total cost
                    room.totalCost = room.materials.reduce((sum, mat) => {
                      const materialCost = (mat.price || 0) * (mat.area || roomArea);
                      const serviceCost = (mat.servicePrice || 0) * (mat.area || roomArea);
                      
                      // Add dependent materials cost
                      let totalDependentCost = 0;
                      (mat.dependentMaterials || []).forEach(dep => {
                        const depCost = ((dep.price || 0) * (mat.area || roomArea));
                        const depServiceCost = ((dep.servicePrice || 0) * (mat.area || roomArea));
                        totalDependentCost += depCost + depServiceCost;
                      });
                      
                      // Add linked dependent materials
                      let totalLinkedCost = 0;
                      (mat.linkedDependentMaterials || []).forEach(link => {
                        if (!link.material) return;
                        
                        const linkQuantity = parseFloat(link.quantity || 1);
                        const depPrice = parseFloat(link.material.price || 0);
                        const depServicePrice = parseFloat(link.material.servicePrice || 0);
                        const linkCost = ((depPrice + depServicePrice) * linkQuantity * (mat.area || roomArea));
                        totalLinkedCost += linkCost;
                      });
                      
                      const totalItemCost = materialCost + serviceCost + totalDependentCost + totalLinkedCost;
                      return sum + totalItemCost;
                    }, 0);
                    
                    return {
                      roomType: room.name,
                      materials: room.materials.map(mat => ({
                        ...mat,
                        // Ensure isDefault is preserved
                        isDefault: mat.isDefault === true
                      })),
                      area: roomArea,
                      totalCost: room.materials.reduce((sum, mat) => {
                        const materialCost = (mat.price || 0) * (mat.area || roomArea);
                        const serviceCost = (mat.servicePrice || 0) * (mat.area || roomArea);
                        const dependentCosts = (mat.dependentMaterials || []).reduce((depSum, dep) => 
                          depSum + ((dep.price || 0) * (mat.area || roomArea)), 0);
                        
                        // Add linked dependent costs
                        const linkedDependentCosts = (mat.linkedDependentMaterials || []).reduce((linkSum, link) => {
                          if (!link.material) return linkSum;
                          const linkQuantity = parseFloat(link.quantity || 1);
                          const depPrice = parseFloat(link.material.price || 0);
                          const depServicePrice = parseFloat(link.material.servicePrice || 0);
                          return linkSum + ((depPrice + depServicePrice) * linkQuantity * (mat.area || roomArea));
                        }, 0);
                        
                        return sum + materialCost + serviceCost + dependentCosts + linkedDependentCosts;
                      }, 0)
                    };
                  }),
                  fixtures: initialFixtures // Add fixtures here
                },
                costs: {
                  materialsCost: parsedMaterial.roomTypes.reduce((sum, room) => 
                    sum + room.materials.reduce((matSum, mat) => {
                      // Calculate main material cost
                      const materialCost = ((mat.price || 0) * (mat.area || 1));
                      
                      // Calculate dependent materials cost
                      const dependentCost = (mat.dependentMaterials || []).reduce((depSum, dep) => {
                        return depSum + ((dep.price || 0) * (mat.area || 1));
                      }, 0);
                        
                      // Calculate linked dependent materials cost
                      const linkedDependentCost = (mat.linkedDependentMaterials || []).reduce((linkSum, link) => {
                        if (!link.material) return linkSum;
                        const linkQuantity = parseFloat(link.quantity || 1);
                        const depPrice = parseFloat(link.material.price || 0);
                        return linkSum + (depPrice * linkQuantity * (mat.area || 1));
                      }, 0);
                      
                      return matSum + materialCost + dependentCost + linkedDependentCost;
                    }, 0), 0),
                  serviceCost: parsedMaterial.roomTypes.reduce((sum, room) => 
                    sum + room.materials.reduce((matSum, mat) => {
                      // Calculate main service cost
                      const serviceCost = ((mat.servicePrice || 0) * (mat.area || 1));
                      
                      // Calculate dependent materials service cost
                      const dependentServiceCost = (mat.dependentMaterials || []).reduce((depSum, dep) => 
                        depSum + ((dep.servicePrice || 0) * (mat.area || 1)), 0);
                        
                      // Calculate linked dependent materials service cost
                      const linkedDependentServiceCost = (mat.linkedDependentMaterials || []).reduce((linkSum, link) => {
                        if (!link.material) return linkSum;
                        const linkQuantity = parseFloat(link.quantity || 1);
                        const depServicePrice = parseFloat(link.material.servicePrice || 0);
                        return linkSum + (depServicePrice * linkQuantity * (mat.area || 1));
                      }, 0);
                      
                      return matSum + serviceCost + dependentServiceCost + linkedDependentServiceCost;
                    }, 0), 0),
                  currency: parsedMaterial.roomTypes[0]?.materials[0]?.currency || 'AZN'
                }
              };
              
              // Calculate general cost
              breakdownData.costs.generalCost = 
                breakdownData.costs.materialsCost + 
                breakdownData.costs.serviceCost;
              
              // Add fixture costs to the main costs
              if (initialFixtures.doors) {
                breakdownData.costs.materialsCost += initialFixtures.doors.materialCost || 0;
                breakdownData.costs.serviceCost += initialFixtures.doors.serviceCost || 0;
                breakdownData.costs.generalCost += (initialFixtures.doors.materialCost || 0) + (initialFixtures.doors.serviceCost || 0);
              }
              if (initialFixtures.windows) {
                breakdownData.costs.materialsCost += initialFixtures.windows.materialCost || 0;
                breakdownData.costs.serviceCost += initialFixtures.windows.serviceCost || 0;
                breakdownData.costs.generalCost += (initialFixtures.windows.materialCost || 0) + (initialFixtures.windows.serviceCost || 0);
              }
              

              
              setBreakdownData(breakdownData);
              setRooms(breakdownData.breakdown.rooms);
              setFilteredRooms(breakdownData.breakdown.rooms);
              setCosts(breakdownData.costs);
              
              // Fetch material details for store information
              await fetchMaterialDetails(breakdownData);
              
              // Fetch door and window prices
              await fetchDoorWindowPrices();
            } catch (parseError) {
              setError("Material məlumatları zədələnib");
              return;
            }
            setIsLoading(false);
            return;
          }

          // If no material data found, try localStorage as fallback
          let tempData = localStorage.getItem(`breakdown_${id}`);
          if (!tempData) {
            tempData = localStorage.getItem(id);
          }
          
          
          
          if (tempData) {
            try {
              const parsedData = JSON.parse(tempData);
              
              // Handle both data formats
              const breakdownData = parsedData.breakdown ? parsedData : { breakdown: parsedData };
              
              setBreakdownData(breakdownData);
              if (breakdownData.breakdown && breakdownData.breakdown.rooms) {
                setRooms(breakdownData.breakdown.rooms);
                setFilteredRooms(breakdownData.breakdown.rooms);
              }
              if (breakdownData.costs || parsedData.costs) {
                setCosts(breakdownData.costs || parsedData.costs);
              }
              
              // Fetch material details for store information
              await fetchMaterialDetails(breakdownData);
              
              // Fetch door and window prices
              await fetchDoorWindowPrices();
            } catch (parseError) {
              setError("Hesablama məlumatları zədələnib");
              return;
            }
          } else {
            setError("Hesablama məlumatları tapılmadı");
            return;
          }
          setIsLoading(false);
          return;
        }
        
        // For non-temporary IDs, make API call
        const response = await axiosWithAuth().get(`${getApiEndpoint('repairs/breakdown-materials')}/${id}`);
        if (response.data) {
          // Process the API response to ensure room areas are set correctly
          if (response.data.breakdown && response.data.breakdown.rooms) {
            // Update rooms with calculated area values if missing
            response.data.breakdown.rooms = response.data.breakdown.rooms.map(room => {
              // If room already has a valid area, use it
              if (room.area && parseFloat(room.area) > 0) {
                return room;
              }
              
              // Otherwise calculate area from materials
              let calculatedArea = 0;
              
              if (Array.isArray(room.materials) && room.materials.length > 0) {
                // Sum up areas from all materials
                calculatedArea = room.materials.reduce((sum, material) => {
                  const materialArea = parseFloat(material.area || 0);
                  return sum + (isNaN(materialArea) || materialArea <= 0 ? 1 : materialArea);
                }, 0);
              }
              
              // Ensure we have a minimum of 1 m²
              calculatedArea = Math.max(calculatedArea, 1);
              
              // Return updated room with area
              return {
                ...room,
                area: calculatedArea
              };
            });
          }
          
          setBreakdownData(response.data);
          if (response.data.breakdown && response.data.breakdown.rooms) {
            setRooms(response.data.breakdown.rooms);
            setFilteredRooms(response.data.breakdown.rooms);
          }
          if (response.data.costs) {
            setCosts(response.data.costs);
          }
          
          // Fetch material details for store information
          await fetchMaterialDetails(response.data);
          
          // Fetch door and window prices
          await fetchDoorWindowPrices();
        }
      } catch (error) {
        setError(error.message || "Məlumatları yükləyərkən xəta baş verdi");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id]); // Remove accessToken and navigate from dependencies to prevent re-fetching

  // Auto-select first room when data is loaded - only run once when rooms are first loaded
  useEffect(() => {
    if (isBreakdown && rooms.length > 0 && !selectedRoom) {
              const firstRoom = {
          id: (rooms[0].name || '').replace(/\s+/g, '-').toLowerCase(),
          name: rooms[0].name || ''
        };
      setSelectedRoom(firstRoom);
      setFilteredRooms([rooms[0]]);
    }
  }, [isBreakdown, rooms]);

  // Remove automatic breakdown parameter addition to prevent infinite loops

  const handleRoomChange = (selectedRoom) => {
    setSelectedRoom(selectedRoom);
    
    if (selectedRoom && isBreakdown && rooms.length > 0) {
      const roomIndex = rooms.findIndex(room => {
        // Add defensive check for undefined room name
        if (!room || !room.name) return false;
        return (room.name || '').replace(/\s+/g, '-').toLowerCase() === selectedRoom.id;
      });
      
      if (roomIndex !== -1) {
        setFilteredRooms([rooms[roomIndex]]);
      } else {
        setFilteredRooms([]);
      }
    } else if (selectedRoom) {
      const filtered = rooms.filter(
        room => room.name && (room.name || '').replace(/\s+/g, '-').toLowerCase() === selectedRoom.id
      );
      setFilteredRooms(filtered);
    } else {
      setFilteredRooms([]);
    }
  };

  const getUnitLabel = (unit) => {
    const unitMap = {
      'm2': 'm²',
      'm3': 'm³',
      'lm': 'm',
      'pcs': 'adet',
      'set': 'set',
      'lt': 'lt'
    };
    
    return unitMap[unit] || unit;
  };

  // Add styles to head
  useEffect(() => {
    // Add custom styles to hide material area details
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      .material-area, .material-quantity {
        display: none !important;
        opacity: 0 !important;
        height: 0 !important;
        overflow: hidden !important;
      }
      
      /* Hide area details in table cells */
      .column.name-column div div {
        display: none !important;
        opacity: 0 !important;
      }
    `;
    document.head.appendChild(styleElement);
    
    // Cleanup function
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);
  
  // Remove auto-save effect that was causing loops

  const renderMaterialRow = (material, depth = 0, parentArea = 1, isDefault = false) => {
    
    // Get the room area from the parent room
    const room = filteredRooms[0];
    
    // Get room values from sessionStorage to ensure correct area values
    const roomValues = JSON.parse(sessionStorage.getItem("roomValues") || "[]");
    
    // Find the correct room data and area - prioritize from multiple sources
                            const roomData = room?.name ? roomValues.find(r => r.name === room.name) : null;
    let roomArea = roomData ? parseFloat(roomData.area) : null;
    
    // If not found in roomValues, try the room object directly
    if (!roomArea && room && room.area) {
      roomArea = parseFloat(room.area);
    }
    
    // If still not found, try the material area
    if (!roomArea && material.area) {
      roomArea = parseFloat(material.area);
    }
    
    // Fallback to 15 if no area is found (consistent with the shown HTML output)
    if (isNaN(roomArea) || roomArea <= 0) {
      roomArea = 15;
    }
    
    // IMPORTANT: The material could have isDefault=true, or it could be passed as isDefault=true from the parent call
    // Make an explicit stringified check for true here to ensure both Boolean true and string "true" are caught
    const materialIsDefault = material.isDefault === true || String(material.isDefault) === "true";
    // Check if this is a default material or was marked as default in a parent call
    const isDefaultMaterial = isDefault || materialIsDefault;
    
    // Check if this is a linked dependent material
    const isLinkedDependent = material.isLinkedDependentMaterial === true;
    
    const quantity = parseFloat(material.quantity || material.count || 1);
    const price = parseFloat(material.price || 0);
    const servicePrice = parseFloat(material.servicePrice || 0);
    
    // Use 1 as fallback only if usagePerSquareMeter is null, undefined, or NaN
    let usagePerSquareMeter = parseFloat(material.usagePerSquareMeter);
    if (isNaN(usagePerSquareMeter)) {
      usagePerSquareMeter = 1;
    }
    
    // Calculate total cost using room area and usagePerSquareMeter
    // Material cost: totalArea * usagePerSquareMeter * price
    const materialCost = price * quantity * roomArea * usagePerSquareMeter;
    
    return (
      <React.Fragment key={`mat-${depth}-${material.id || material.materialId || Math.random().toString(36).substring(2, 9)}`}>
        <div className={`table-row${depth > 0 ? ' dependent-row' : ''}${isDefaultMaterial ? ' default-material-row' : ''}${isLinkedDependent ? ' linked-dependent-row' : ''}`}>
          <div className="column name-column">
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
            <span className="material-name">
              {material.materialName || (typeof material.name === 'object' ? material.name.text || material.name.az : material.name)}
            </span>
              
              <div style={{ display: 'none', opacity: 0, gap: '8px', fontSize: '0.8rem', color: '#666', marginTop: '2px' }}>
            {quantity > 1 && (
              <span className="material-quantity">
                ({quantity} units)
              </span>
            )}
            {roomArea > 1 && (
                  <span className="material-area">
                (x{roomArea} m²)
              </span>
            )}
              </div>
            </div>
          </div>
          <div className="column store-column">
            {(() => {
              const materialId = material.materialId || material.id;
              
              // First check if we have material details from API
              if (materialDetails[materialId] && materialDetails[materialId].stores && materialDetails[materialId].stores.length > 0) {
                const firstStore = materialDetails[materialId].stores[0];
                return typeof firstStore.name === 'object' 
                  ? (firstStore.name.az || firstStore.name.en || firstStore.name.text)
                  : firstStore.name;
              }
              
              // Try different ways to get store information from material itself
              if (material.store?.name) {
                return typeof material.store.name === 'object' 
                  ? (material.store.name.az || material.store.name.en || material.store.name.text)
                  : material.store.name;
              }
              
              if (material.storeName) {
                return typeof material.storeName === 'object'
                  ? (material.storeName.az || material.storeName.en || material.storeName.text)
                  : material.storeName;
              }
              
              // Check if stores array exists and has items
              if (material.stores && Array.isArray(material.stores) && material.stores.length > 0) {
                const firstStore = material.stores[0];
                if (typeof firstStore === 'string') {
                  return firstStore;
                } else if (firstStore.name) {
                  return typeof firstStore.name === 'object' 
                    ? (firstStore.name.az || firstStore.name.en || firstStore.name.text)
                    : firstStore.name;
                }
              }
              
              // Check for store information in other potential fields
              if (material.storeInfo?.name) {
                return typeof material.storeInfo.name === 'object'
                  ? (material.storeInfo.name.az || material.storeInfo.name.en || material.storeInfo.name.text)
                  : material.storeInfo.name;
              }
              
              return t("material.noStore");
            })()}
          </div>
          <div className="column price-column" style={{display: 'none', opacity: 0}}>
            {price.toFixed(2)}
          </div>
          <div className="column amount-column" style={{display: 'none', opacity: 0}}>
            {materialCost.toFixed(2)}
          </div>
        </div>
        
        {/* Only render dependent materials if this is NOT a default material itself to prevent duplicates */}
        {!isDefaultMaterial && material.dependentMaterials?.map((depMaterial, index) => {
          return renderMaterialRow(depMaterial, depth + 1, roomArea, depMaterial.isDefault === true);
        })}
        
        {/* Only render linked dependent materials if this is NOT a default material itself to prevent duplicates */}
        {!isDefaultMaterial && material.linkedDependentMaterials?.map((link, index) => {
          if (!link.material) return null;
          
          const linkQuantity = parseFloat(link.quantity || 1);
          const depWithQuantity = {
            ...link.material,
            id: link.material.id || `linked-${material.id || material.materialId || index}-${index}`,
            count: linkQuantity,
            quantity: linkQuantity,
            isLinkedDependentMaterial: true
          };
          
          return renderMaterialRow(depWithQuantity, depth + 1, roomArea, link.material.isDefault === true);
        })}
      </React.Fragment>
    );
  };

  // Handle login redirect for saving
  const handleLoginRedirect = () => {
    setIsRedirectingToLogin(true);
    
    try {
      // Save current path for redirect after login
      const currentPath = window.location.pathname + window.location.search;
      
      // Check if user is coming from Material page
      const referrer = document.referrer;
      const isFromMaterialPage = referrer && referrer.includes('/material');
      if (isFromMaterialPage) {
        localStorage.setItem('comingFromMaterialPage', 'true');
        console.log("User is coming from Material page:", referrer);
      }
      
      // Store any temporary material data before redirecting
      if (id) {
        localStorage.setItem('pendingRepairId', id);
        localStorage.setItem('redirectToAllMaterials', 'true');
        console.log("Storing material ID before login redirect:", id);
        
        // If it's a temporary ID, store the data
        if (id.startsWith('temp-')) {
        const tempData = localStorage.getItem(id);
        if (tempData) {
          localStorage.setItem('lastRepairData', tempData);
          
            try {
          const parsedData = JSON.parse(tempData);
          
          if (parsedData.property) {
            localStorage.setItem('lastRepairProperty', JSON.stringify(parsedData.property));
          }
          
          if (parsedData.materials) {
            localStorage.setItem('lastRepairMaterials', JSON.stringify(parsedData.materials));
          }
          
              if (parsedData.costs) {
                localStorage.setItem('lastRepairMaterialCost', parsedData.costs.materialsCost || 0);
                localStorage.setItem('lastRepairServiceCost', parsedData.costs.serviceCost || 0);
                localStorage.setItem('lastRepairGeneralCost', parsedData.costs.generalCost || 0);
              } else {
                // Backward compatibility with older format
          localStorage.setItem('lastRepairMaterialCost', parsedData.materialsCost || 0);
          localStorage.setItem('lastRepairServiceCost', parsedData.serviceCost || 0);
          localStorage.setItem('lastRepairGeneralCost', parsedData.generalCost || 0);
        }
            } catch (parseError) {
              console.error("Error parsing temp data before login:", parseError);
            }
          }
        }
      }
      
      // Also store any selected material data from session storage
      const selectedMaterialData = sessionStorage.getItem('selectedMaterial');
      if (selectedMaterialData) {
        localStorage.setItem('pendingSelectedMaterial', selectedMaterialData);
      }
      
      // Store breakdown data if available
      if (breakdownData) {
        localStorage.setItem('pendingBreakdownData', JSON.stringify(breakdownData));
      }
    } catch (e) {
      console.error("Error saving material data before login redirect:", e);
    }
    
    // Short delay to show the redirect message
    setTimeout(() => {
      // Navigate to login with return URL
      const currentPath = window.location.pathname + window.location.search;
    navigate('/login', { state: { returnUrl: currentPath } });
    }, 1000);
  };

  // Check if user is logged in
  const isUserLoggedIn = () => {
    return !!accessToken;
  };

  if (isLoading) {
    return (
      <div className="loader-container">
        <SquareLoader color="#cda742" size={80} />
      </div>
    );
  }

  if (isRedirectingToLogin) {
    return (
      <div className="loader-container">
        <Header
          title={headerTitle}
          showProfileIcon={false}
          showBackIcon={true}
          onBackClick={() => navigate(backwardNavigate)}
        />
        <div className="d-flex flex-column align-items-center justify-content-center p-4">
          <SquareLoader color="#cda742" size={50} />
          <h5 className="mt-4 text-center">Giriş səhifəsinə yönləndirilirsiniz...</h5>
          <p className="text-center text-muted mt-2">
            Bu səhifəni görmək üçün giriş etməlisiniz
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <Header
          title={headerTitle}
          showProfileIcon={true}
          showBackIcon={true}
          onBackClick={() => navigate(backwardNavigate)}
        />
        <div className="alert alert-danger m-3">
          {error}
        </div>
        <div className="d-flex justify-content-center m-3 flex-column align-items-center">
          <button 
            className="btn py-2 global-button mb-3"
            onClick={() => window.location.reload()}
          >
            Yenidən yoxla
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="allmaterials-section">
      <Header
        title={headerTitle}
        showProfileIcon={true}
        showBackIcon={true}
        onBackClick={() => navigate(backwardNavigate)}
      />
      

    
      {/* Property Info - for repair view */}
      {property && !isBreakdown && (
        <div className="property-header">
          <div className="property-title">
            <h2>{property.propertyType?.name?.text || "Mülk"}</h2>
            <span className="property-area">{property.area} m²</span>
          </div>
          <div className="property-details">
            <span className="detail-item">
              <i className="fas fa-door-open" style={{ width: '1em', height: '1em' }}></i> {property.doorCount || 0}
            </span>
            <span className="detail-item">
              <i className="fas fa-window-maximize" style={{ width: '1em', height: '1em' }}></i> {property.windowCount || 0}
            </span>
            <span className="detail-item">
              <i className="fas fa-th-large" style={{ width: '1em', height: '1em' }}></i> {property.roomCount || 0}
            </span>
          </div>
        </div>
      )}
      
      {/* Breakdown summary - for breakdown view */}
      {isBreakdown && costs && (
        <div className="breakdown-summary" style={{display: 'none', opacity: 0}}>
          <div className="summary-card">
            <div className="summary-title">{t("material.materials")}</div>
            <div className="summary-value">
              {(costs.materialsCost || 0).toFixed(2)} {costs.currency || "AZN"}
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-title">{t("material.service")}</div>
            <div className="summary-value">
              {(costs.serviceCost || 0).toFixed(2)} {costs.currency || "AZN"}
            </div>
          </div>
          <div className="summary-card total">
            <div className="summary-title">{t("material.total")}</div>
            <div className="summary-value">
              {(costs.generalCost || 0).toFixed(2)} {costs.currency || "AZN"}
            </div>
          </div>
        </div>
      )}

      <div className="materials-content">
        <div className="top-section">
          <h5>{t("material.0")}</h5>
          <p className="subtitle">{t("material.1")}</p>
        </div>
        
        {/* Show message for temporary ID */}
        {id && id.startsWith('temp-') && (
          <div className="middle-section">
            <div className="alert alert-info">
              {t("maintenance.temporaryCalculation")}
            </div>
          </div>
        )}
        
        <div className="middle-section">
          {rooms.length > 0 ? (
            <RoomDropdown
              onChange={handleRoomChange}
              selectedRoomTypes={isBreakdown 
                ? rooms.map(room => ({
                    id: (room.name || '').replace(/\s+/g, '-').toLowerCase(),
                    name: room.name || ''
                  }))
                : rooms.map(room => room.name || '')
              }
              selectedValue={selectedRoom}
            />
          ) : (
            <div className="no-rooms-message">
              {t("material.7")}
            </div>
          )}
        </div>
        
        {/* Materials listing - breakdown view */}
        {isBreakdown && filteredRooms.length > 0 && (
          <div className="bottom-section breakdown-view">
            <div className="room-header">
                  <h6>
                    {filteredRooms[0]?.name || 'Otaq'} - 
                    <span className="room-area">
                      {(() => {
                        // Get the room
                        const room = filteredRooms[0];
                        if (!room) return '16 m²';
                        
                        // Get room values from sessionStorage to ensure correct area values
                        const roomValues = JSON.parse(sessionStorage.getItem("roomValues") || "[]");
                        
                        // Find the correct room data and area
                        const roomData = room.name ? roomValues.find(r => r.name === room.name) : null;
                        let roomArea = roomData ? parseFloat(roomData.area) : null;
                        
                        // If not found in roomValues, try the room object directly
                        if (!roomArea && room && room.area) {
                          roomArea = parseFloat(room.area);
                        }
                        
                        // Fallback to 15 if no area is found (consistent with the shown HTML output)
                        if (isNaN(roomArea) || roomArea <= 0) {
                          roomArea = 15;
                        }
                        
                        return `${roomArea} m²`;
                      })()}
                    </span>
                  </h6>
            </div>
            
            <div className="materials-table">
              <div className="table-header">
                <div className="column name-column">{t("material.2")}</div>
                    <div className="column store-column">{t("material.store")}</div>
                <div className="column price-column" style={{display: 'none', opacity: 0}}>{t("material.price")}</div>
                <div className="column amount-column" style={{display: 'none', opacity: 0}}>{t("material.total")}</div>
              </div>
              
              <div className="table-body">
                {filteredRooms[0].materials && filteredRooms[0].materials.map((material) => {
                  const isDefaultMaterial = material.isDefault === true;
                  return renderMaterialRow(material, 0, 1, isDefaultMaterial);
                })}
              </div>
            </div>
            
            {/* Room total */}
            <div className="room-total" style={{display: 'none', opacity: 0}}>
              <span>{t("material.total")}:</span>
                  <span className="total-price">
                    {(() => {
                      const room = filteredRooms[0];
                      if (!room) return "0.00";
                      
                      // Get room values from sessionStorage to ensure correct area values
                      const roomValues = JSON.parse(sessionStorage.getItem("roomValues") || "[]");
                      
                      // Find the correct room data and area
                      const roomData = room.name ? roomValues.find(r => r.name === room.name) : null;
                      let roomArea = roomData ? parseFloat(roomData.area) : null;
                      
                      // If not found in roomValues, try the room object directly
                      if (!roomArea && room && room.area) {
                        roomArea = parseFloat(room.area);
                      }
                      
                      // Fallback to 15 if no area is found (consistent with the shown HTML output)
                      if (isNaN(roomArea) || roomArea <= 0) {
                        roomArea = 15;
                      }
                      
                      // Calculate material cost only (not including service)
                      let totalMaterialCost = 0;
                      let totalServiceCost = 0; // Servis maliyeti için değişken tanımlıyoruz
                      
                      // Add main materials costs (materials only, not service)
                      room.materials?.forEach(material => {
                        const quantity = parseFloat(material.quantity || material.count || 1);
                        const price = parseFloat(material.price || 0);
                        const servicePrice = parseFloat(material.servicePrice || 0); // Servis fiyatını alıyoruz
                        
                        // Use 1 as fallback only if usagePerSquareMeter is null, undefined, or NaN
                        let usagePerSquareMeter = parseFloat(material.usagePerSquareMeter);
                        if (isNaN(usagePerSquareMeter)) {
                          usagePerSquareMeter = 1;
                        }
                        
                        // Material cost: totalArea * usagePerSquareMeter * price
                        totalMaterialCost += price * quantity * roomArea * usagePerSquareMeter;
                        
                        // Service cost: totalArea * servicePrice (no usagePerSquareMeter)
                        totalServiceCost += servicePrice * quantity * roomArea;
                        
                        // Add dependent materials costs (materials only, not service)
                        material.dependentMaterials?.forEach(depMat => {
                          const depQuantity = parseFloat(depMat.quantity || depMat.count || 1);
                          const depPrice = parseFloat(depMat.price || 0);
                          const depServicePrice = parseFloat(depMat.servicePrice || 0); // Bağımlı malzeme servis fiyatını alıyoruz
                          
                          // Use 1 as fallback only if usagePerSquareMeter is null, undefined, or NaN
                          let depUsagePerSquareMeter = parseFloat(depMat.usagePerSquareMeter);
                          if (isNaN(depUsagePerSquareMeter)) {
                            depUsagePerSquareMeter = 1;
                          }
                          
                          // Dependent material cost: totalArea * usagePerSquareMeter * price
                          totalMaterialCost += depPrice * depQuantity * roomArea * depUsagePerSquareMeter;
                          
                          // Dependent service cost: totalArea * servicePrice (no usagePerSquareMeter)
                          totalServiceCost += depServicePrice * depQuantity * roomArea;
                        });
                          
                          // Add linked dependent materials costs (materials only, not service)
                          material.linkedDependentMaterials?.forEach(link => {
                            if (!link.material) return;
                            
                            const linkQuantity = parseFloat(link.quantity || 1);
                            const depPrice = parseFloat(link.material.price || 0);
                            const depServicePrice = parseFloat(link.material.servicePrice || 0); // Bağlantılı malzeme servis fiyatını alıyoruz
                            
                            // Use 1 as fallback only if usagePerSquareMeter is null, undefined, or NaN
                            let depUsagePerSquareMeter = parseFloat(link.material.usagePerSquareMeter);
                            if (isNaN(depUsagePerSquareMeter)) {
                              depUsagePerSquareMeter = 1;
                            }
                            
                            // Linked dependent material cost: totalArea * usagePerSquareMeter * price
                            totalMaterialCost += depPrice * linkQuantity * roomArea * depUsagePerSquareMeter;
                            
                            // Linked dependent service cost: totalArea * servicePrice (no usagePerSquareMeter)
                            totalServiceCost += depServicePrice * linkQuantity * roomArea;
                        });
                      });
                      
                      // Add fixtures costs for this room using current door/window prices
                      const fixtures = breakdownData?.breakdown?.fixtures || {};
                      if (fixtures.doors && fixtures.doors.count > 0) {
                        const doorCount = fixtures.doors.count;
                        // Kapı malzeme maliyeti
                        totalMaterialCost += doorCount * doorWindowPrices.doorPrice;
                        // Kapı hizmet maliyeti
                        totalServiceCost += doorCount * doorWindowPrices.doorServicePrice;
                      }
                      if (fixtures.windows && fixtures.windows.count > 0) {
                        const windowCount = fixtures.windows.count;
                        // Pencere malzeme maliyeti
                        totalMaterialCost += windowCount * doorWindowPrices.windowPrice;
                        // Pencere hizmet maliyeti
                        totalServiceCost += windowCount * doorWindowPrices.windowServicePrice;
                      }
                      
                      return totalMaterialCost.toFixed(2);
                    })()} {costs?.currency || "AZN"}
                  </span>
                </div>
                
                {/* Detailed Cost Breakdown */}
                <div className="cost-breakdown-section mt-3">
                  <div className="alert alert-info">
                    <h6 className="mb-2">Qiymət təfərrüatları:</h6>
                    <div className="cost-details">
                      {(() => {
                        const room = filteredRooms[0];
                        if (!room) return null;
                        
                        // Get room values from sessionStorage to ensure correct area values
                        const roomValues = JSON.parse(sessionStorage.getItem("roomValues") || "[]");
                        
                        // Find the correct room data and area
                        const roomData = room.name ? roomValues.find(r => r.name === room.name) : null;
                        let roomArea = roomData ? parseFloat(roomData.area) : null;
                        
                        // If not found in roomValues, try the room object directly
                        if (!roomArea && room && room.area) {
                          roomArea = parseFloat(room.area);
                        }
                        
                        // Fallback to 15 if no area is found (consistent with the shown HTML output)
                        if (isNaN(roomArea) || roomArea <= 0) {
                          roomArea = 15;
                        }
                        
                        // Get material details from first material for display
                        const firstMaterial = room.materials && room.materials[0];
                        const unitMaterialPrice = firstMaterial ? parseFloat(firstMaterial.price || 0) : 0;
                        const unitServicePrice = firstMaterial ? parseFloat(firstMaterial.servicePrice || 0) : 0;
                        const currency = costs?.currency || firstMaterial?.currency || "AZN";
                        
                        // Calculate separate costs
                        let totalMaterialCost = 0;
                        let totalServiceCost = 0;
                          let totalDependentMaterialCost = 0;
                          let totalDependentServiceCost = 0;
                          let totalLinkedDependentMaterialCost = 0;
                          let totalLinkedDependentServiceCost = 0;
                        
                        // Add main costs
                        room.materials?.forEach(material => {
                          const quantity = parseFloat(material.quantity || material.count || 1);
                          const price = parseFloat(material.price || 0);
                          const servicePrice = parseFloat(material.servicePrice || 0);
                          
                          // Use 1 as fallback only if usagePerSquareMeter is null, undefined, or NaN
                          let usagePerSquareMeter = parseFloat(material.usagePerSquareMeter);
                          if (isNaN(usagePerSquareMeter)) {
                            usagePerSquareMeter = 1;
                          }
                          
                          // Material cost: totalArea * usagePerSquareMeter * price
                          totalMaterialCost += price * quantity * roomArea * usagePerSquareMeter;
                          
                          // Service cost: totalArea * servicePrice (no usagePerSquareMeter)
                          totalServiceCost += servicePrice * quantity * roomArea;
                            
                            // Calculate dependent materials costs separately
                            material.dependentMaterials?.forEach(depMat => {
                              const depQuantity = parseFloat(depMat.quantity || depMat.count || 1);
                              const depPrice = parseFloat(depMat.price || 0);
                              const depServicePrice = parseFloat(depMat.servicePrice || 0);
                              
                              // Use 1 as fallback only if usagePerSquareMeter is null, undefined, or NaN
                              let depUsagePerSquareMeter = parseFloat(depMat.usagePerSquareMeter);
                              if (isNaN(depUsagePerSquareMeter)) {
                                depUsagePerSquareMeter = 1;
                              }
                              
                              // Dependent material cost: totalArea * usagePerSquareMeter * price
                              totalDependentMaterialCost += depPrice * depQuantity * roomArea * depUsagePerSquareMeter;
                              
                              // Dependent service cost: totalArea * servicePrice (no usagePerSquareMeter)
                              totalDependentServiceCost += depServicePrice * depQuantity * roomArea;
                            });
                            
                            // Calculate linked dependent materials costs separately
                            material.linkedDependentMaterials?.forEach(link => {
                              if (!link.material) return;
                              
                              const linkQuantity = parseFloat(link.quantity || 1);
                              const depPrice = parseFloat(link.material.price || 0);
                              const depServicePrice = parseFloat(link.material.servicePrice || 0);
                              
                              // Use 1 as fallback only if usagePerSquareMeter is null, undefined, or NaN
                              let depUsagePerSquareMeter = parseFloat(link.material.usagePerSquareMeter);
                              if (isNaN(depUsagePerSquareMeter)) {
                                depUsagePerSquareMeter = 1;
                              }
                              
                              // Linked dependent material cost: totalArea * usagePerSquareMeter * price
                              totalLinkedDependentMaterialCost += depPrice * linkQuantity * roomArea * depUsagePerSquareMeter;
                              
                              // Linked dependent service cost: totalArea * servicePrice (no usagePerSquareMeter)
                              totalLinkedDependentServiceCost += depServicePrice * linkQuantity * roomArea;
                            });
                          });
                          
                          // Calculate grand totals
                          const totalBaseCost = totalMaterialCost + totalServiceCost;
                          const totalDependentCost = totalDependentMaterialCost + totalDependentServiceCost;
                          const totalLinkedDependentCost = totalLinkedDependentMaterialCost + totalLinkedDependentServiceCost;
                          const grandTotalCost = totalBaseCost + totalDependentCost + totalLinkedDependentCost;
                        
                        return (
                          <div>
                            <div className="small text-muted mb-2">
                              <strong>Otaq sahəsi:</strong> {roomArea} m²
                            </div>

                            {roomArea !== 1 && firstMaterial && (
                              <div className="border-bottom pb-2 mb-3" style={{display: 'none'}}>
                                <div className="row mb-1 small">
                                  <div className="col-7">Material vahid qiyməti:</div>
                                  <div className="col-5 text-end">{unitMaterialPrice.toFixed(2)} {currency}/m²</div>
                                </div>
                                <div className="row mb-1 small">
                                  <div className="col-7">Xidmət vahid qiyməti:</div>
                                  <div className="col-5 text-end">{unitServicePrice.toFixed(2)} {currency}/m²</div>
                                </div>
                              </div>
                            )}
                            
                            <div className="row mb-1">
                                <div className="col-8">Əsas material qiyməti:</div>
                              <div className="col-4 text-end">
                                {totalMaterialCost.toFixed(2)} {currency}
                              </div>
                            </div>
                            <div className="row mb-1">
                                <div className="col-8">Əsas xidmət qiyməti:</div>
                              <div className="col-4 text-end">
                                {totalServiceCost.toFixed(2)} {currency}
                              </div>
                            </div>
                              
                              {(totalDependentMaterialCost > 0 || totalDependentServiceCost > 0) && (
                                <>
                                  <div className="row mb-1 mt-2 pt-2 border-top">
                                    <div className="col-8">Bağlı material qiyməti:</div>
                                    <div className="col-4 text-end">
                                      {totalDependentMaterialCost.toFixed(2)} {currency}
                                    </div>
                                  </div>
                                  <div className="row mb-1">
                                    <div className="col-8">Bağlı xidmət qiyməti:</div>
                                    <div className="col-4 text-end">
                                      {totalDependentServiceCost.toFixed(2)} {currency}
                                    </div>
                                  </div>
                                </>
                              )}
                              
                              {(totalLinkedDependentMaterialCost > 0 || totalLinkedDependentServiceCost > 0) && (
                                <>
                                  <div className="row mb-1 mt-2 pt-2 border-top">
                                    <div className="col-8">Əlavə bağlı material qiyməti:</div>
                                    <div className="col-4 text-end">
                                      {totalLinkedDependentMaterialCost.toFixed(2)} {currency}
                                    </div>
                                  </div>
                                  <div className="row mb-1">
                                    <div className="col-8">Əlavə bağlı xidmət qiyməti:</div>
                                    <div className="col-4 text-end">
                                      {totalLinkedDependentServiceCost.toFixed(2)} {currency}
                                    </div>
                                  </div>
                                </>
                              )}
                              
                            <div className="row fw-bold mt-2 pt-2 border-top">
                              <div className="col-8">Ümumi qiymət:</div>
                              <div className="col-4 text-end">
                                  {grandTotalCost.toFixed(2)} {currency}
                              </div>
                            </div>
                            
                            <div className="small text-muted mt-3">
                              * Qiymətlər sahəyə görə hesablanır.
                              {roomArea !== 1 ? ` Yuxarıdakı qiymətlər ${roomArea} m² üçün hesablanmışdır.` : ''}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
            </div>
            
            {/* Fixtures */}
            {breakdownData?.breakdown?.fixtures && (
              <div className="fixtures-section">
                <h6>{t("material.fixtures")}</h6>
                
                <div className="materials-table">
                  <div className="table-header">
                    <div className="column name-column">{t("material.item")}</div>
                    <div className="column count-column">{t("material.count")}</div>
                    <div className="column amount-column" style={{display: 'none', opacity: 0}}>{t("material.cost")}</div>
                  </div>
                  
                      {/* Doors */}
                      {breakdownData.breakdown.fixtures.doors && 
                       (breakdownData.breakdown.fixtures.doors.count > 0 || 
                        breakdownData.breakdown.fixtures.doors.materialCost > 0 ||
                        breakdownData.breakdown.fixtures.doors.serviceCost > 0) && (
                    <div className="table-row">
                      <div className="column name-column">{t("material.doors")}</div>
                      <div className="column count-column">{breakdownData.breakdown.fixtures.doors.count || 0}</div>
                      <div className="column amount-column" style={{display: 'none', opacity: 0}}>
                        <div>Material: {doorWindowPrices.doorPrice.toFixed(2)} {costs?.currency || "AZN"}</div>
                        <div>Xidmət: {doorWindowPrices.doorServicePrice.toFixed(2)} {costs?.currency || "AZN"}</div>
                      </div>
                    </div>
                  )}
                  
                      {/* Windows */}
                      {breakdownData.breakdown.fixtures.windows && 
                       (breakdownData.breakdown.fixtures.windows.count > 0 ||
                        breakdownData.breakdown.fixtures.windows.materialCost > 0 ||
                        breakdownData.breakdown.fixtures.windows.serviceCost > 0) && (
                    <div className="table-row">
                      <div className="column name-column">{t("material.windows")}</div>
                      <div className="column count-column">{breakdownData.breakdown.fixtures.windows.count || 0}</div>
                      <div className="column amount-column" style={{display: 'none', opacity: 0}}>
                        <div>Material: {doorWindowPrices.windowPrice.toFixed(2)} {costs?.currency || "AZN"}</div>
                        <div>Xidmət: {doorWindowPrices.windowServicePrice.toFixed(2)} {costs?.currency || "AZN"}</div>
                          </div>
                        </div>
                      )}
                      
                      {/* Eğer kapı ve pencere yoksa bilgi mesajı göster */}
                      {(!breakdownData.breakdown.fixtures.doors || 
                        (breakdownData.breakdown.fixtures.doors.count <= 0 && 
                         breakdownData.breakdown.fixtures.doors.materialCost <= 0 &&
                         breakdownData.breakdown.fixtures.doors.serviceCost <= 0)) &&
                       (!breakdownData.breakdown.fixtures.windows || 
                        (breakdownData.breakdown.fixtures.windows.count <= 0 &&
                         breakdownData.breakdown.fixtures.windows.materialCost <= 0 &&
                         breakdownData.breakdown.fixtures.windows.serviceCost <= 0)) && (
                        <div className="table-row">
                          <div className="column name-column" style={{textAlign: "center", flex: "1"}}>
                            {t("material.noFixtures")}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Original materials listing - repair view */}
        {!isBreakdown && filteredRooms.length > 0 && (
        <div className="bottom-section">
            <div className="materials-table">
              <div className="table-header">
                <div className="column name-column">{t("material.2")}</div>
                <div className="column amount-column">{t("material.3")}</div>
              </div>
              <div className="table-body">
                {filteredRooms.map((room, roomIndex) => (
                  room.roomComponents.map((component, compIndex) => (
                    <div className="table-row" key={`${roomIndex}-${compIndex}`}>
                      <div className="column name-column">
                        {component.material?.name?.text || component.material?.title || t("material.4")}
                      </div>
                      <div className="column amount-column">
                        {component.consumption || 0} {getUnitLabel(component.material?.unitOfMeasure || "m2")}
                      </div>
                    </div>
                  ))
                ))}
              </div>
            </div>
          </div>
        )}
        
        {        /* Show message if no rooms or materials are found */}
        {rooms.length === 0 ? (
          <div className="no-materials alert alert-warning">
                {t("material.noMaterialsSelected")}
            <div className="mt-3">
        <button 
                className="btn btn-outline-primary" 
                onClick={() => window.location.href = "/material/select"}
        >
                Materialları seçin
          </button>
            </div>
          </div>
        ) : null}

        {/* Save button for temporary calculations */}
        {id && id.startsWith('temp-') && !isUserLoggedIn() && (
          <div className="save-section mt-4 mb-4">
            <div className="alert alert-info mb-3">
              <p className="mb-2">{t("maintenance.temporaryNotSaved")}</p>
              <p className="mb-0 small">{t("maintenance.loginToSave")}</p>
            </div>
            <div className="d-flex justify-content-center">
              <button 
                className="btn py-3 global-button"
                onClick={handleLoginRedirect}
              >
                Məlumatları yadda saxla
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllMaterials;
